/**
 * Copyright 2026 Ziz Academy
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { examApi, adminUsersApi, institutionsApi, examFolderApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Exam, ExamAssignment, User, ExamFolder } from '../types';
import Questions from './Questions';
import Evaluations from './Evaluations';
import ExamsTable from '../components/ExamsTable';

type AdminTab = 'exams' | 'questions' | 'assignments' | 'drafts' | 'evaluations' | 'users' | 'institutions';

const AdminDashboard: React.FC = () => {
    const [folderTree, setFolderTree] = useState<ExamFolder[]>([]);
    const [foldersLoading, setFoldersLoading] = useState<boolean>(false);
    const [newFolderName, setNewFolderName] = useState<string>('');
    const [newFolderParentId, setNewFolderParentId] = useState<number | null>(null);
    const [exams, setExams] = useState<Exam[]>([]);
    const [drafts, setDrafts] = useState<Exam[]>([]);
    const [assignments, setAssignments] = useState<ExamAssignment[]>([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState<AdminTab>('exams');
    const [users, setUsers] = useState<User[]>([]);
    const [institutions, setInstitutions] = useState<{ id: number; name: string }[]>([]);
    const [selectedInstitutionId, setSelectedInstitutionId] = useState<number>(() => {
        const saved = localStorage.getItem('admin_selected_institution_id');
        return saved ? Number(saved) : 0; // 0 means ALL
    });
    const [showAddUserForm, setShowAddUserForm] = useState(false);
    const [showEditUserForm, setShowEditUserForm] = useState(false);
    const [newUser, setNewUser] = useState<{ username: string; name: string; email: string; password: string; role: User['role']; institutionId?: number }>({ username: '', name: '', email: '', password: 'password123', role: 'STUDENT' });
    const [editUser, setEditUser] = useState<{ id?: number; name: string; email?: string; role: User['role']; institutionId?: number }>({ name: '', role: 'STUDENT' });
    const [formErrors, setFormErrors] = useState<{ [k: string]: string }>({});
    const [editFormErrors, setEditFormErrors] = useState<{ [k: string]: string }>({});
    const [editPassword, setEditPassword] = useState<string>('');
    const [editPasswordConfirm, setEditPasswordConfirm] = useState<string>('');
    const [submitting, setSubmitting] = useState(false);
    const [showAddInstitutionForm, setShowAddInstitutionForm] = useState(false);
    const [newInstitutionName, setNewInstitutionName] = useState('');
    const [editingInstitutionId, setEditingInstitutionId] = useState<number | null>(null);
    const [editingInstitutionName, setEditingInstitutionName] = useState('');
    const { user } = useAuth();

    const loadFolders = async () => {
        if (!user?.id) return;
        try {
            setFoldersLoading(true);
            const tree = await examFolderApi.getTree(user.id);
            setFolderTree(tree);
        } catch (e) {
            console.error('Failed to load folders', e);
        } finally {
            setFoldersLoading(false);
        }
    };

    const createFolder = async () => {
        if (!user?.id) return;
        const name = newFolderName.trim();
        if (!name) { alert('Please enter a folder name'); return; }
        try {
            await examFolderApi.create(user.id, name, newFolderParentId ?? null);
            setNewFolderName('');
            setNewFolderParentId(null);
            await loadFolders();
        } catch (e) {
            console.error('Failed to create folder', e);
            alert('Could not create folder.');
        }
    };

    const moveExamToFolder = async (examId: number, folderId: number | null) => {
        if (!user?.id) return;
        try {
            await examFolderApi.moveExam(user.id, examId, folderId ?? null);
            // Refresh exams list to reflect folder assignment
            const list = await examApi.getAllExamsForAdmin(user.id);
            setExams((list || []).filter((e: any) => e.status !== 'DRAFT'));
        } catch (e) {
            console.error('Failed to move exam', e);
            alert('Could not move exam to folder.');
        }
    };

    const flattenFolders = (nodes: ExamFolder[], level = 0): { id: number | null; label: string }[] => {
        const res: { id: number | null; label: string }[] = [];
        for (const n of nodes) {
            res.push({ id: n.id, label: `${'— '.repeat(level)}${n.name}` });
            if (n.children && n.children.length > 0) {
                res.push(...flattenFolders(n.children, level + 1));
            }
        }
        return res;
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [examsData, assignmentsData, draftsData, preloadUsers] = await Promise.all([
                    user?.id ? examApi.getAllExamsForAdmin(user.id, user.role === 'SUPER_ADMIN' ? selectedInstitutionId : undefined) : Promise.resolve([]),
                    user?.id ? examApi.getAllAssignmentsForAdmin(user.id, user.role === 'SUPER_ADMIN' ? selectedInstitutionId : undefined) : Promise.resolve([]),
                    user?.id ? examApi.getDraftsForAdmin(user.id, user.role === 'SUPER_ADMIN' ? selectedInstitutionId : undefined) : Promise.resolve([]),
                    (user?.id && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN')) ? adminUsersApi.list(user.id, user.role === 'SUPER_ADMIN' ? selectedInstitutionId : undefined) : Promise.resolve([] as User[]),
                ]);
                if (preloadUsers && Array.isArray(preloadUsers)) {
                    setUsers(preloadUsers as User[]);
                }
                // Keep published/archived in exams tab; drafts in drafts tab
                setExams((examsData || []).filter(e => e.status !== 'DRAFT'));
                setAssignments(assignmentsData || []);
                setDrafts(draftsData || []);
                if (user?.role === 'SUPER_ADMIN') {
                    const insts = await institutionsApi.list();
                    setInstitutions(insts as any);
                }
            } catch (error) {
                console.error('Error fetching admin data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
        loadFolders();
        // re-run when institution filter changes or user changes
    }, [user?.id, user?.role, selectedInstitutionId]);

    // Lazy-load lists for new tabs
    useEffect(() => {
        const loadForTab = async () => {
            try {
                if (activeTab === 'users' && user?.id) {
                    // If users are already preloaded, avoid duplicate fetch unless list is empty
                    if (users.length === 0) {
                        const list = await adminUsersApi.list(user.id, user.role === 'SUPER_ADMIN' ? selectedInstitutionId : undefined);
                        setUsers(list);
                    }
                    if (user.role === 'SUPER_ADMIN') {
                        const insts = await institutionsApi.list();
                        setInstitutions(insts as any);
                    }
                } else if (activeTab === 'institutions' && user?.role === 'SUPER_ADMIN') {
                    const insts = await institutionsApi.list();
                    setInstitutions(insts as any);
                }
            } catch (e) {
                console.error('Error loading data for tab', activeTab, e);
            }
        };
        loadForTab();
    }, [activeTab, user?.id, user?.role]);

    const reloadUsers = async () => {
        if (!user?.id) return;
        const list = await adminUsersApi.list(user.id, user.role === 'SUPER_ADMIN' ? selectedInstitutionId : undefined);
        setUsers(list);
    };

    const reloadInstitutions = async () => {
        const insts = await institutionsApi.list();
        setInstitutions(insts as any);
    };

    const validateNewUser = (): boolean => {
        const errs: { [k: string]: string } = {};
        if (!newUser.username || newUser.username.trim().length < 3 || newUser.username.trim().length > 50) {
            errs.username = 'Username must be between 3 and 50 characters';
        }
        if (!newUser.name || newUser.name.trim().length === 0) {
            errs.name = 'Name is required';
        }
        if (newUser.email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(newUser.email)) {
            errs.email = 'Invalid email format';
        }
        if (newUser.password && newUser.password.length < 6) {
            errs.password = 'Password must be at least 6 characters';
        }
        if (user?.role === 'SUPER_ADMIN') {
            if (!newUser.institutionId) {
                errs.institutionId = 'Please select an institution';
            }
        }
        setFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const submitAddUser = async () => {
        if (!user?.id) return;
        if (!validateNewUser()) return;
        setSubmitting(true);
        try {
            const payload: any = { ...newUser };
            await adminUsersApi.create(user.id, payload);
            setShowAddUserForm(false);
            setNewUser({ username: '', name: '', email: '', password: 'password123', role: 'STUDENT' });
            setFormErrors({});
            await reloadUsers();
        } catch (e: any) {
            console.error('Create user failed', e);
            const msg = e?.response?.data?.message || e?.response?.data || 'Failed to create user';
            setFormErrors(prev => ({ ...prev, _server: String(msg) }));
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditUser = async (u: User) => {
        if (!user?.id) return;
        if (user.role === 'SUPER_ADMIN' && institutions.length === 0) {
            await reloadInstitutions();
        }
        setEditUser({ id: u.id, name: u.name, email: u.email, role: u.role, institutionId: u.institutionId });
        setEditPassword('');
        setEditPasswordConfirm('');
        setEditFormErrors({});
        setShowEditUserForm(true);
    };

    const validateEditUser = (): boolean => {
        const errs: { [k: string]: string } = {};
        if (!editUser.name || editUser.name.trim().length === 0) {
            errs.name = 'Name is required';
        }
        if (editUser.email && !/^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/.test(editUser.email)) {
            errs.email = 'Invalid email format';
        }
        // Optional password change validation
        if (editPassword && editPassword.length < 6) {
            errs.password = 'Password must be at least 6 characters';
        }
        if (editPassword || editPasswordConfirm) {
            if (editPassword !== editPasswordConfirm) {
                errs.passwordConfirm = 'Passwords do not match';
            }
        }
        if (user?.role === 'SUPER_ADMIN') {
            if (!editUser.institutionId) {
                errs.institutionId = 'Please select an institution';
            }
        }
        setEditFormErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const submitEditUser = async () => {
        if (!user?.id || !editUser.id) return;
        if (!validateEditUser()) return;
        setSubmitting(true);
        try {
            const payload: any = { name: editUser.name, email: editUser.email, role: editUser.role };
            if (user.role === 'SUPER_ADMIN') {
                payload.institutionId = editUser.institutionId;
            }
            if (editPassword) {
                payload.password = editPassword;
            }
            await adminUsersApi.update(user.id, editUser.id, payload);
            setShowEditUserForm(false);
            setEditUser({ id: undefined, name: '', email: '', role: 'STUDENT', institutionId: undefined });
            setEditPassword('');
            setEditPasswordConfirm('');
            setEditFormErrors({});
            await reloadUsers();
        } catch (e: any) {
            console.error('Update user failed', e);
            const msg = e?.response?.data?.message || e?.response?.data || 'Failed to update user';
            setEditFormErrors(prev => ({ ...prev, _server: String(msg) }));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteUser = async (u: User) => {
        if (!user?.id) return;
        if (!window.confirm(`Delete user ${u.username}?`)) return;
        try {
            await adminUsersApi.delete(user.id, u.id);
            setUsers(prev => prev.filter(x => x.id !== u.id));
        } catch (e) {
            console.error('Delete user failed', e);
            window.alert('Failed to delete user');
        }
    };

    const handleAddInstitution = async () => {
        setShowAddInstitutionForm(true);
        setNewInstitutionName('');
    };

    const submitAddInstitution = async () => {
        if (!newInstitutionName || newInstitutionName.trim().length === 0) {
            alert('Institution name is required');
            return;
        }
        try {
            await institutionsApi.create(newInstitutionName.trim());
            setShowAddInstitutionForm(false);
            setNewInstitutionName('');
            await reloadInstitutions();
        } catch (e) {
            console.error('Create institution failed', e);
            alert('Failed to create institution');
        }
    };

    const handleEditInstitution = async (inst: { id: number; name: string }) => {
        setEditingInstitutionId(inst.id);
        setEditingInstitutionName(inst.name);
    };

    const submitEditInstitution = async () => {
        if (!editingInstitutionId) return;
        if (!editingInstitutionName || editingInstitutionName.trim().length === 0) {
            alert('Institution name is required');
            return;
        }
        try {
            await institutionsApi.update(editingInstitutionId, editingInstitutionName.trim());
            setEditingInstitutionId(null);
            setEditingInstitutionName('');
            await reloadInstitutions();
        } catch (e) {
            console.error('Update institution failed', e);
            alert('Failed to update institution');
        }
    };

    const cancelEditInstitution = () => {
        setEditingInstitutionId(null);
        setEditingInstitutionName('');
    };

    const handleDeleteInstitution = async (inst: { id: number; name: string }) => {
        if (!window.confirm(`Delete institution ${inst.name}?`)) return;
        try {
            await institutionsApi.delete(inst.id);
            setInstitutions(prev => prev.filter(x => x.id !== inst.id));
        } catch (e) {
            console.error('Delete institution failed', e);
            window.alert('Failed to delete institution');
        }
    };

    const handleDeleteExam = async (examId: number) => {
        if (window.confirm('Are you sure you want to delete this exam?')) {
            try {
                await examApi.delete(examId);
                setExams(exams.filter((e) => e.id !== examId));
            } catch (error) {
                console.error('Error deleting exam:', error);
            }
        }
    };

    if (loading) {
        return (
            <div className="loading" data-testid="loading">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div data-testid="admin-dashboard">
            <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <h1 style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>
                    👨‍💼 Admin Dashboard
                </h1>
                <p style={{ fontSize: '1.25rem', opacity: 0.9 }}>
                    Manage all exams and submissions across the platform 🎓
                </p>
            </div>

            {/* Tab Navigation */}
            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem', borderBottom: '2px solid #e2e8f0', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button onClick={() => setActiveTab('exams')} className={`btn btn-sm ${activeTab === 'exams' ? 'btn-primary' : 'btn-outline'}`} type="button">
                        📋 All Exams ({exams.length})
                    </button>
                    <button onClick={() => setActiveTab('questions')} className={`btn btn-sm ${activeTab === 'questions' ? 'btn-primary' : 'btn-outline'}`} type="button">
                        ❓ Questions
                    </button>
                    <button onClick={() => setActiveTab('assignments')} className={`btn btn-sm ${activeTab === 'assignments' ? 'btn-primary' : 'btn-outline'}`} type="button">
                        🧾 All Assigned Exams ({assignments.length})
                    </button>
                    <button onClick={() => setActiveTab('drafts')} className={`btn btn-sm ${activeTab === 'drafts' ? 'btn-primary' : 'btn-outline'}`} type="button">
                        🗂 Drafts ({drafts.length})
                    </button>
                    <button onClick={() => setActiveTab('evaluations')} className={`btn btn-sm ${activeTab === 'evaluations' ? 'btn-primary' : 'btn-outline'}`} type="button">
                        🧮 Evaluations
                    </button>
                    {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                        <button onClick={() => setActiveTab('users')} className={`btn btn-sm ${activeTab === 'users' ? 'btn-primary' : 'btn-outline'}`} type="button">
                            👤 Users ({users.length})
                        </button>
                    )}
                    {user?.role === 'SUPER_ADMIN' && (
                        <button onClick={() => setActiveTab('institutions')} className={`btn btn-sm ${activeTab === 'institutions' ? 'btn-primary' : 'btn-outline'}`} type="button">
                            🏫 Institutions ({institutions.length})
                        </button>
                    )}
                </div>
                {user?.role === 'SUPER_ADMIN' && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <label style={{ fontSize: 12, color: '#555' }}>Institution Scope:</label>
                        <select
                            className="select select-sm select-bordered"
                            value={String(selectedInstitutionId)}
                            onChange={(e) => {
                                const val = Number(e.target.value);
                                setSelectedInstitutionId(val);
                                localStorage.setItem('admin_selected_institution_id', String(val));
                            }}
                            title="Filter data by institution or choose ALL"
                        >
                            <option value={0}>ALL</option>
                            {institutions.map(i => (
                                <option key={i.id} value={i.id}>{i.name}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* Questions Tab (inline) */}
            {activeTab === 'questions' && (
                <div>
                    <Questions />
                </div>
            )}

            {/* Evaluations Tab (inline) */}
            {activeTab === 'evaluations' && (
                <div>
                    <Evaluations />
                </div>
            )}

            {/* All Exams Tab */}
            {activeTab === 'exams' && (
                <div>
                    <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            📋 All Exams from All Teachers
                        </h2>
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div className="form-control" style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                <input
                                    type="text"
                                    className="form-input"
                                    placeholder="New folder name"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    style={{ minWidth: 200, height: '40px' }}
                                />
                                <select
                                    className="form-input"
                                    value={String(newFolderParentId ?? 'null')}
                                    onChange={(e) => setNewFolderParentId(e.target.value === 'null' ? null : Number(e.target.value))}
                                    title="Parent folder (optional)"
                                    style={{ height: '40px' }}
                                >
                                    <option value="null">Root folder</option>
                                    {flattenFolders(folderTree).map(opt => (
                                        <option key={String(opt.id)} value={String(opt.id)}>{opt.label}</option>
                                    ))}
                                </select>
                                <button className="btn btn-outline" onClick={createFolder} disabled={foldersLoading}>
                                    {foldersLoading ? 'Creating...' : '➕ Create Folder'}
                                </button>
                            </div>
                            {(user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN') && (
                                <Link to="/create-exam" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>
                                    ➕ Create Exam
                                </Link>
                            )}
                        </div>
                    </div>

                    {exams.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-emoji">📝</div>
                            <h3 className="empty-state-title">No Exams Found</h3>
                            <p>There are no exams in the system yet.</p>
                        </div>
                    ) : (
                        <ExamsTable
                            exams={exams}
                            showCreatedBy={true}
                            showInstitution={true}
                            showStatus={true}
                            showEdit={true}
                            showAssign={true}
                            showEvaluate={true}
                            showPreview={true}
                            showDownload={true}
                            folderOptions={flattenFolders(folderTree)}
                            onMoveToFolder={(examId, folderId) => moveExamToFolder(examId, folderId)}
                            onDelete={(id) => handleDeleteExam(id)}
                        />
                    )}
                </div>
            )}

            {/* All Assignments Tab */}
            {activeTab === 'assignments' && (
                <div>
                    <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        🧾 All Exams Assigned to Students
                    </h2>

                    {assignments.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-emoji">📭</div>
                            <h3 className="empty-state-title">No Assignments Found</h3>
                            <p>No exams have been assigned yet.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Exam</th>
                                        <th>Teacher</th>
                                        <th>Institution</th>
                                        <th>Student</th>
                                        <th>Username</th>
                                        <th>Email</th>
                                        <th>Assigned At</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {assignments.map((a) => (
                                        <tr key={a.assignmentId}>
                                            <td>{a.examTitle}</td>
                                            <td>{a.examCreatedByName ?? '-'}</td>
                                            <td>{a.institutionName || '-'}</td>
                                            <td>{a.studentName}</td>
                                            <td>{a.studentUsername}</td>
                                            <td>{a.studentEmail}</td>
                                            <td>{a.assignedAt ? new Date(a.assignedAt).toLocaleString() : '-'}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Drafts Tab */}
            {activeTab === 'drafts' && (
                <div>
                    <h2 style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        🗂 All Draft Exams
                    </h2>

                    {drafts.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-emoji">🗂</div>
                            <h3 className="empty-state-title">No Drafts Found</h3>
                            <p>Teachers can save exams as draft during creation.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Title</th>
                                        <th>Teacher</th>
                                        <th>Duration</th>
                                        <th>Total Marks</th>
                                        <th>Created</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {drafts.map((exam) => (
                                        <tr key={exam.id}>
                                            <td><strong>{exam.title}</strong></td>
                                            <td>{exam.createdByName ?? '-'}</td>
                                            <td>{exam.durationMinutes ?? '-'}</td>
                                            <td>{exam.totalMarks ?? '-'}</td>
                                            <td>{exam.createdAt ? new Date(exam.createdAt).toLocaleDateString() : '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <Link to={`/edit-exam/${exam.id}`} className="btn btn-outline btn-sm" style={{ whiteSpace: 'nowrap' }}>
                                                        ✏️ Edit
                                                    </Link>
                                                    <button
                                                        className="btn btn-danger btn-sm"
                                                        onClick={() => handleDeleteExam(exam.id!)}
                                                        style={{ whiteSpace: 'nowrap' }}
                                                    >
                                                        🗑 Delete
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Users Tab */}
            {activeTab === 'users' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h2 style={{ margin: 0 }}>👤 Users</h2>
                        {!showAddUserForm ? (
                            <button
                                className="btn btn-primary"
                                onClick={async () => {
                                    if (user?.role === 'SUPER_ADMIN' && institutions.length === 0) {
                                        await reloadInstitutions();
                                    }
                                    setShowAddUserForm(true);
                                }}
                            >
                                ➕ Add User
                            </button>
                        ) : (
                            <button className="btn btn-outline" onClick={() => setShowAddUserForm(false)}>✖ Cancel</button>
                        )}
                    </div>

                    {showAddUserForm && (
                        <div className="card" style={{ marginBottom: '1rem' }}>
                            <div className="form-grid-2">
                                <div className="form-field">
                                    <label>Username *</label>
                                    <input
                                        type="text"
                                        value={newUser.username}
                                        onChange={e => setNewUser(prev => ({ ...prev, username: e.target.value }))}
                                    />
                                    {formErrors.username && <div className="error-text">{formErrors.username}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Full Name *</label>
                                    <input
                                        type="text"
                                        value={newUser.name}
                                        onChange={e => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                    {formErrors.name && <div className="error-text">{formErrors.name}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={newUser.email}
                                        onChange={e => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                                    />
                                    {formErrors.email && <div className="error-text">{formErrors.email}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={newUser.password}
                                        onChange={e => setNewUser(prev => ({ ...prev, password: e.target.value }))}
                                        placeholder="password123"
                                    />
                                    {formErrors.password && <div className="error-text">{formErrors.password}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Role *</label>
                                    <select
                                        value={newUser.role}
                                        onChange={e => setNewUser(prev => ({ ...prev, role: e.target.value as User['role'] }))}
                                    >
                                        <option value="STUDENT">STUDENT</option>
                                        <option value="TEACHER">TEACHER</option>
                                        <option value="ADMIN">ADMIN</option>
                                    </select>
                                </div>
                                {user?.role === 'SUPER_ADMIN' ? (
                                    <div className="form-field">
                                        <label>Institution *</label>
                                        <select
                                            value={newUser.institutionId ?? ''}
                                            onChange={e => setNewUser(prev => ({ ...prev, institutionId: e.target.value ? Number(e.target.value) : undefined }))}
                                        >
                                            <option value="">-- Select Institution --</option>
                                            {institutions.map(i => (
                                                <option key={i.id} value={i.id}>{i.name}</option>
                                            ))}
                                        </select>
                                        {formErrors.institutionId && <div className="error-text">{formErrors.institutionId}</div>}
                                    </div>
                                ) : (
                                    <div className="form-field">
                                        <label>Institution</label>
                                        <input type="text" value={user?.institutionName || 'Open'} readOnly />
                                    </div>
                                )}
                            </div>
                            {formErrors._server && (
                                <div className="error-text" style={{ marginTop: '0.5rem' }}>{formErrors._server}</div>
                            )}
                            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitAddUser}>
                                    {submitting ? 'Saving...' : 'Save User'}
                                </button>
                                <button className="btn btn-outline" disabled={submitting} onClick={() => setShowAddUserForm(false)}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {showEditUserForm && (
                        <div className="card" style={{ marginBottom: '1rem' }}>
                            <div className="form-grid-2">
                                <div className="form-field">
                                    <label>Full Name *</label>
                                    <input
                                        type="text"
                                        value={editUser.name}
                                        onChange={e => setEditUser(prev => ({ ...prev, name: e.target.value }))}
                                    />
                                    {editFormErrors.name && <div className="error-text">{editFormErrors.name}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Email</label>
                                    <input
                                        type="email"
                                        value={editUser.email || ''}
                                        onChange={e => setEditUser(prev => ({ ...prev, email: e.target.value }))}
                                    />
                                    {editFormErrors.email && <div className="error-text">{editFormErrors.email}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Password</label>
                                    <input
                                        type="password"
                                        value={editPassword}
                                        onChange={e => setEditPassword(e.target.value)}
                                        placeholder="Leave blank to keep unchanged"
                                    />
                                    {editFormErrors.password && <div className="error-text">{editFormErrors.password}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Confirm Password</label>
                                    <input
                                        type="password"
                                        value={editPasswordConfirm}
                                        onChange={e => setEditPasswordConfirm(e.target.value)}
                                        placeholder="Re-enter new password"
                                    />
                                    {editFormErrors.passwordConfirm && <div className="error-text">{editFormErrors.passwordConfirm}</div>}
                                </div>
                                <div className="form-field">
                                    <label>Role *</label>
                                    <select
                                        value={editUser.role}
                                        onChange={e => setEditUser(prev => ({ ...prev, role: e.target.value as User['role'] }))}
                                    >
                                        <option value="STUDENT">STUDENT</option>
                                        <option value="TEACHER">TEACHER</option>
                                        <option value="ADMIN">ADMIN</option>
                                    </select>
                                </div>
                                {user?.role === 'SUPER_ADMIN' && (
                                    <div className="form-field">
                                        <label>Institution *</label>
                                        <select
                                            value={editUser.institutionId ?? ''}
                                            onChange={e => setEditUser(prev => ({ ...prev, institutionId: e.target.value ? Number(e.target.value) : undefined }))}
                                        >
                                            <option value="">-- Select Institution --</option>
                                            {institutions.map(i => (
                                                <option key={i.id} value={i.id}>{i.name}</option>
                                            ))}
                                        </select>
                                        {editFormErrors.institutionId && <div className="error-text">{editFormErrors.institutionId}</div>}
                                    </div>
                                )}
                            </div>
                            {editFormErrors._server && (
                                <div className="error-text" style={{ marginTop: '0.5rem' }}>{editFormErrors._server}</div>
                            )}
                            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-primary" disabled={submitting} onClick={submitEditUser}>
                                    {submitting ? 'Saving...' : 'Save Changes'}
                                </button>
                                <button className="btn btn-outline" disabled={submitting} onClick={() => setShowEditUserForm(false)}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {users.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-emoji">👥</div>
                            <h3 className="empty-state-title">No Users Found</h3>
                            <p>Use the Add button to create a new user.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Username</th>
                                        <th>Name</th>
                                        <th>Email</th>
                                        <th>Role</th>
                                        <th>Institution</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map((u) => (
                                        <tr key={u.id}>
                                            <td>{u.username}</td>
                                            <td>{u.name}</td>
                                            <td>{u.email}</td>
                                            <td>{u.role}</td>
                                            <td>{u.institutionName || '-'}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <button className="btn btn-outline btn-sm" onClick={() => handleEditUser(u)}>✏️ Edit</button>
                                                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(u)}>🗑 Delete</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            {/* Institutions Tab (Super Admin only) */}
            {activeTab === 'institutions' && user?.role === 'SUPER_ADMIN' && (
                <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                        <h2 style={{ margin: 0 }}>🏫 Institutions</h2>
                        {!showAddInstitutionForm ? (
                            <button className="btn btn-primary" onClick={handleAddInstitution}>➕ Add Institution</button>
                        ) : (
                            <button className="btn btn-outline" onClick={() => { setShowAddInstitutionForm(false); setNewInstitutionName(''); }}>✖ Cancel</button>
                        )}
                    </div>

                    {showAddInstitutionForm && (
                        <div className="card" style={{ marginBottom: '1rem' }}>
                            <div className="form-grid-2">
                                <div className="form-field">
                                    <label>Institution Name *</label>
                                    <input type="text" value={newInstitutionName} onChange={e => setNewInstitutionName(e.target.value)} />
                                </div>
                            </div>
                            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-primary" onClick={submitAddInstitution}>Save Institution</button>
                                <button className="btn btn-outline" onClick={() => { setShowAddInstitutionForm(false); setNewInstitutionName(''); }}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {editingInstitutionId && (
                        <div className="card" style={{ marginBottom: '1rem' }}>
                            <div className="form-grid-2">
                                <div className="form-field">
                                    <label>Edit Institution Name *</label>
                                    <input type="text" value={editingInstitutionName} onChange={e => setEditingInstitutionName(e.target.value)} />
                                </div>
                            </div>
                            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
                                <button className="btn btn-primary" onClick={submitEditInstitution}>Save Changes</button>
                                <button className="btn btn-outline" onClick={cancelEditInstitution}>Cancel</button>
                            </div>
                        </div>
                    )}

                    {institutions.length === 0 ? (
                        <div className="empty-state">
                            <div className="empty-state-emoji">🏫</div>
                            <h3 className="empty-state-title">No Institutions Found</h3>
                            <p>Use the Add button to create a new institution.</p>
                        </div>
                    ) : (
                        <div className="table-container">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Name</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {institutions.map((i) => (
                                        <tr key={i.id}>
                                            <td>{i.name}</td>
                                            <td>
                                                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                                                    <button className="btn btn-outline btn-sm" onClick={() => handleEditInstitution(i)}>✏️ Edit</button>
                                                    <button className="btn btn-danger btn-sm" onClick={() => handleDeleteInstitution(i)}>🗑 Delete</button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default AdminDashboard;
